import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, BASE, FEEDBACK_KINDS, FEEDBACK_STATUS, type Changelog, type Feedback, type MeDetails } from '../lib/api';
import { ago, dNum } from '../lib/domain';
import { Avatar, ErrorBox, Icon, Loading, PageHead } from '../ui';
import { allPushOff, disablePush, enablePush, pushOffOnLogout, pushState, type PushState } from '../lib/push';
import { installOption, onInstallChange, promptInstall, type InstallOption } from '../lib/install';
import { useToast } from '../hooks';
import { useBarTitle } from '../barTitle';

interface SessionRow {
  id: string;
  device: string;
  created_at: string;
  last_seen_at: string;
  current: boolean;
}

export default function Profil() {
  useBarTitle('Profil'); // also while loading
  const me = useQuery({ queryKey: ['me', 'details'], queryFn: () => api<MeDetails>('/me') });
  if (me.isLoading) return <Loading />;
  if (me.error || !me.data) return <ErrorBox error={me.error} />;
  return <ProfilBody data={me.data} />;
}

function ProfilBody({ data }: { data: MeDetails }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const toast = useToast();
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: () => api<SessionRow[]>('/me/sessions') });
  const [f, setF] = useState<Record<string, string>>({
    first_name: data.first_name,
    last_name: data.last_name,
    phone: data.phone || '',
    street: data.street || '',
    house_number: data.house_number || '',
    zip: data.zip || '',
    city: data.city || '',
  });
  const save = useMutation({
    mutationFn: (body: Record<string, string>) => api('/me', { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: ['members'] }); // name in the member list
      void qc.invalidateQueries({ queryKey: ['member'] });
      toast('Profil gespeichert');
    },
    onError: e => toast(e.message),
  });
  const endSession = useMutation({
    mutationFn: (id: string) => api(`/me/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });
  const logout = useMutation({
    mutationFn: async () => {
      await pushOffOnLogout(); // this device stops getting my pushes
      return api('/auth/logout', { method: 'POST' });
    },
    onSuccess: () => {
      qc.clear();
      void nav('/login', { replace: true });
    },
  });

  const field = (k: string, label: string, props: Record<string, unknown> = {}) => (
    <label className="fl">
      <span>{label}</span>
      <input value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} {...props} />
    </label>
  );
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (!f.first_name.trim() || !f.last_name.trim()) return toast('Bitte Vor- und Nachname eingeben');
    save.mutate(f);
  };

  return (
    <>
      <PageHead title="Profil" />
      <div className="layout">
        <div style={{ minWidth: 0, display: 'grid', gap: 22 }}>
          <NewsHint />
          <section className="panel">
            <p className="eyebrow">Profil</p>
            <div style={{ display: 'flex', gap: 18, alignItems: 'center', margin: '6px 0 14px' }}>
              <Avatar
                person={{ id: data.id, name: `${data.first_name} ${data.last_name}`, photo: data.photo }}
                size="lg"
              />
              <div>
                <b>
                  {data.first_name} {data.last_name}
                </b>
                <div className="muted">{data.email}</div>
                {data.role === 'admin' && <span className="st st-new">Admin</span>}
                <PhotoButtons hasPhoto={!!data.photo} />
              </div>
            </div>
            <form className="form" onSubmit={submit} noValidate>
              <div className="grid2">
                {field('first_name', 'Vorname', { autoComplete: 'given-name', maxLength: 60 })}
                {field('last_name', 'Nachname', { autoComplete: 'family-name', maxLength: 60 })}
              </div>
              <div className="grid2">
                {field('phone', 'Telefon', { autoComplete: 'tel', inputMode: 'tel', maxLength: 40 })}
              </div>
              <div className="grid3">
                {field('street', 'Straße', { autoComplete: 'address-line1', maxLength: 120 })}
                {field('house_number', 'Nr.', { maxLength: 20 })}
              </div>
              <div className="grid3">
                {field('zip', 'PLZ', { autoComplete: 'postal-code', inputMode: 'numeric', maxLength: 10 })}
                {field('city', 'Ort', { autoComplete: 'address-level2', maxLength: 80 })}
              </div>
              <p className="note">Die E-Mail-Adresse ändert die Abteilungsleitung (sie ist deine Anmeldung).</p>
              <div className="form-actions">
                <button className="btn btn-navy" disabled={save.isPending}>
                  Speichern
                </button>
              </div>
            </form>
          </section>
          <section className="panel" id="benachrichtigungen">
            <p className="eyebrow">Benachrichtigungen per E-Mail</p>
            <MailSetting
              field="notify_events"
              initial={data.notify_events}
              label="E-Mail bei neuen und geänderten Terminen und Nachrichten an alle Angemeldeten"
              name="Termin-Mails"
            />
            <MailSetting
              field="notify_market"
              initial={data.notify_market}
              label="E-Mail bei neuen Flohmarkt-Anzeigen"
              name="Flohmarkt-Mails"
            />
            <MailSetting
              field="notify_posts"
              initial={data.notify_posts}
              label="Mitteilungen per E-Mail"
              name="Mitteilungs-Mails"
            />
            <MailSetting
              field="notify_self"
              initial={data.notify_self}
              label="Eigene Termine, Anzeigen und Mitteilungen auch an mich schicken"
              hint="So siehst du, wie deine Mail bei den anderen ankommt."
              name="Kopie an mich"
            />
            <p className="note">
              Anmeldecodes und persönliche Nachrichten (z. B. Fragen zu deiner Anzeige) bekommst du immer.
            </p>
          </section>
          <InstallSection />
          <PushSection data={data} />
          <MyFeedback isAdmin={data.role === 'admin'} />
          <section className="panel">
            <p className="eyebrow">Angemeldete Geräte</p>
            {sessions.data?.map(s => (
              <div className="set-row" key={s.id}>
                <div>
                  <b>
                    {s.device}
                    {s.current ? ' · dieses Gerät' : ''}
                  </b>
                  <p>
                    Zuletzt aktiv {ago(new Date(s.last_seen_at))} · angemeldet seit {dNum(new Date(s.created_at))}
                  </p>
                </div>
                {!s.current && (
                  <button className="btn btn-ghost btn-sm" onClick={() => endSession.mutate(s.id)}>
                    Abmelden
                  </button>
                )}
              </div>
            ))}
            <div className="set-row" style={{ borderBottom: 0 }}>
              <div>
                <b>Auf diesem Gerät abmelden</b>
                <p>Du bleibst sonst dauerhaft angemeldet.</p>
              </div>
              <button className="btn btn-danger btn-sm" onClick={() => logout.mutate()}>
                Abmelden
              </button>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function MyFeedback({ isAdmin }: { isAdmin: boolean }) {
  const mine = useQuery({ queryKey: ['feedback', 'mine'], queryFn: () => api<Feedback[]>('/feedback/mine') });
  return (
    <section className="panel" id="feedback">
      <p className="eyebrow">Mein Feedback</p>
      {mine.data?.length ? (
        <ul className="fb-mine">
          {mine.data.map(f => (
            <li key={f.id}>
              <div className="fb-meta">
                <span className={`chg chg-${f.kind}`}>{FEEDBACK_KINDS[f.kind]}</span>
                <span className={`fb-st fb-st-${f.status}`}>
                  {FEEDBACK_STATUS[f.status]}
                  {f.status === 'done' && f.resolved_version && (
                    <>
                      {' '}
                      in <Link to={`/neu#v${f.resolved_version}`}>Version {f.resolved_version}</Link>
                    </>
                  )}
                </span>
                <span className="muted">{dNum(new Date(f.created_at))}</span>
              </div>
              <p className="fb-body">{f.body}</p>
              {f.note && <p className="fb-answer">Antwort: {f.note}</p>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="note">
          Noch kein Feedback. Über den Knopf <b>Feedback</b> oben kannst du Fehler, Ideen und Fragen schicken.
        </p>
      )}
      <p className="note">
        <Link to="/neu">Was ist neu</Link>
        {isAdmin && (
          <>
            {' · '}
            <Link to="/admin">Admin-Bereich (Feedback, Aktionen, Debug-Log)</Link>
          </>
        )}
      </p>
    </section>
  );
}

/** One on/off switch for a kind of club mail; the shown state changes at once and is reverted if saving fails. */
function MailSetting({
  field,
  initial,
  label,
  hint,
  name,
}: {
  field:
    'notify_events' | 'notify_market' | 'notify_posts' | 'notify_self' | 'push_events' | 'push_market' | 'push_posts';
  initial: boolean;
  label: string;
  hint?: string;
  name: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [on, setOn] = useState(initial);
  const save = useMutation({
    mutationFn: (v: boolean) => api('/me', { method: 'PATCH', body: { [field]: v } }),
    onMutate: v => setOn(v),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      toast(v ? `${name} eingeschaltet` : `${name} ausgeschaltet`);
    },
    onError: (e, v) => {
      setOn(!v);
      toast(e.message);
    },
  });
  return (
    <label className="set-row" style={{ cursor: 'pointer' }}>
      <div>
        <b>{label}</b>
        {hint && <p>{hint}</p>}
      </div>
      <span className="switch">
        <input
          type="checkbox"
          checked={on}
          disabled={save.isPending}
          onChange={e => save.mutate(e.target.checked)}
          aria-label={label}
        />
        <span />
      </span>
    </label>
  );
}

/** Change or remove the own profile picture (square crop on the server, location data removed). */
function PhotoButtons({ hasPhoto }: { hasPhoto: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const refresh = () => {
    for (const key of ['me', 'members', 'member']) void qc.invalidateQueries({ queryKey: [key] });
  };
  async function upload(file: File) {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${BASE}/api/me/photo`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'sportplaner' },
        body: form,
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(data.error || 'Das Foto konnte nicht gespeichert werden.');
      refresh();
      toast('Profilfoto gespeichert');
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const remove = useMutation({
    mutationFn: () => api('/me/photo', { method: 'DELETE' }),
    onSuccess: () => {
      refresh();
      toast('Profilfoto entfernt');
    },
    onError: e => toast(e.message),
  });
  return (
    <div className="photo-btns">
      <label className="btn btn-ghost btn-sm" aria-disabled={busy}>
        {busy ? 'Lädt …' : hasPhoto ? 'Foto ändern' : 'Foto hochladen'}
        <input
          type="file"
          accept="image/*"
          hidden
          disabled={busy}
          aria-label="Profilfoto auswählen"
          onChange={e => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void upload(f);
          }}
        />
      </label>
      {hasPhoto && (
        <button className="btn btn-ghost btn-sm" disabled={remove.isPending} onClick={() => remove.mutate()}>
          Foto entfernen
        </button>
      )}
    </div>
  );
}

/** After an update (the dot at the profile picture): a short banner at the top of the profile */
function NewsHint() {
  const changelog = useQuery({ queryKey: ['changelog'], queryFn: () => api<Changelog>('/changelog') });
  if (!changelog.data?.unseen) return null;
  return (
    <p className="news-hint" role="status">
      <b>Neu:</b> Version {changelog.data.version} – <Link to="/neu">Was ist neu?</Link>
    </p>
  );
}

const PUSH_TEXT: Record<PushState, string> = {
  unsupported: 'Dieser Browser kann keine Push-Nachrichten empfangen.',
  'ios-home-screen':
    'Auf dem iPhone/iPad: unten auf das Teilen-Symbol tippen → „Zum Home-Bildschirm“. Dann den Sportplaner vom Home-Bildschirm aus öffnen und hier Push einschalten.',
  denied:
    'Benachrichtigungen sind für den Sportplaner blockiert. Erlauben lässt sich das in den Einstellungen des Browsers (Website-Einstellungen → Benachrichtigungen).',
  off: 'Neue und geänderte Termine, Flohmarkt-Anzeigen und Mitteilungen zusätzlich als Nachricht auf dieses Gerät. Funktioniert auch im Browser – nur auf dem iPhone muss der Sportplaner zuerst auf den Home-Bildschirm. Jedes Gerät (und jeder Browser) wird einzeln eingeschaltet.',
  on: 'Push ist auf diesem Gerät eingeschaltet.',
};

/** Push on THIS device (each phone/computer is switched on separately) plus what to get as push */
function PushSection({ data }: { data: MeDetails }) {
  const toast = useToast();
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void pushState()
      .then(setState)
      .catch(() => setState('unsupported'));
  }, []);
  const run = async (action: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await action();
      toast(done);
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setState(await pushState().catch(() => 'unsupported' as const));
      setBusy(false);
    }
  };
  return (
    <section className="panel" id="push">
      <p className="eyebrow">Push-Benachrichtigungen auf diesem Gerät</p>
      <p className="note">
        <Link to="/hilfe#push">Wie funktioniert das?</Link>
      </p>
      <p className="note" role="status">
        {state ? PUSH_TEXT[state] : 'Prüfe …'}
      </p>
      <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
        {state === 'off' && (
          <button className="btn btn-navy" disabled={busy} onClick={() => void run(enablePush, 'Push eingeschaltet')}>
            <Icon name="bell" sw={1.8} />
            Auf diesem Gerät einschalten
          </button>
        )}
        {state === 'on' && (
          <>
            <button
              className="btn btn-ghost"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const r = await api<{ devices: number }>('/push/test', { method: 'POST' });
                  if (!r.devices) throw new Error('Kein Gerät erreicht – bitte Push aus- und wieder einschalten.');
                }, 'Test-Nachricht geschickt')
              }
            >
              Test senden
            </button>
            <button
              className="btn btn-ghost"
              disabled={busy}
              onClick={() => void run(disablePush, 'Push ausgeschaltet')}
            >
              Ausschalten
            </button>
          </>
        )}
      </div>
      {state && state !== 'unsupported' && (
        <p className="note">
          <button
            type="button"
            className="link-btn"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const n = await allPushOff();
                if (!n) throw new Error('Push war auf keinem Gerät eingeschaltet.');
              }, 'Push auf allen Geräten ausgeschaltet')
            }
          >
            Push auf allen meinen Geräten ausschalten
          </button>{' '}
          – z. B. wenn ein Handy verloren ging.
        </p>
      )}
      {state === 'on' && (
        <>
          <MailSetting
            field="push_events"
            initial={data.push_events}
            label="Push bei neuen und geänderten Terminen"
            name="Termin-Push"
          />
          <MailSetting
            field="push_market"
            initial={data.push_market}
            label="Push bei neuen Flohmarkt-Anzeigen"
            name="Flohmarkt-Push"
          />
          <MailSetting
            field="push_posts"
            initial={data.push_posts}
            label="Push bei Mitteilungen"
            name="Mitteilungs-Push"
          />
        </>
      )}
    </section>
  );
}

/** "Als App installieren" – only while the Sportplaner is not installed on this device */
function InstallSection() {
  const toast = useToast();
  const [option, setOption] = useState<InstallOption>(installOption);
  useEffect(() => onInstallChange(() => setOption(installOption())), []);
  if (option === 'installed' || option === 'none') return null;
  return (
    <section className="panel" id="app">
      <p className="eyebrow">Als App installieren</p>
      <p className="note">
        Mit eigenem Symbol auf dem Startbildschirm, ohne Adresszeile – und auf dem iPhone die Voraussetzung für
        Push-Benachrichtigungen. <Link to="/hilfe#app">Anleitung für alle Geräte</Link>
      </p>
      {option === 'prompt' && (
        <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
          <button
            className="btn btn-navy"
            onClick={() =>
              void promptInstall().then(ok => {
                if (ok) toast('Sportplaner installiert');
              })
            }
          >
            <Icon name="down" sw={1.8} />
            Als App installieren
          </button>
        </div>
      )}
      {option === 'ios' && (
        <ol className="install-steps">
          <li>
            In Safari unten auf das <b>Teilen-Symbol</b> tippen (Quadrat mit Pfeil nach oben).
          </li>
          <li>
            <b>„Zum Home-Bildschirm“</b> wählen und „Hinzufügen“ tippen.
          </li>
          <li>Den Sportplaner ab jetzt über das neue Symbol öffnen und einmal anmelden.</li>
        </ol>
      )}
      {option === 'android-menu' && (
        <p>
          Im Menü des Browsers (⋮) <b>„App installieren“</b> oder <b>„Zum Startbildschirm hinzufügen“</b> wählen.
        </p>
      )}
    </section>
  );
}
